import React, { useEffect } from 'react';
import { tx, tr } from '../../utils/i18n';
import { routePath } from '../../i18n/routes';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { ArrowRight, Tag, Users, TrendingUp, Shield, Target, Zap } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import Header from '../Header';
import Footer from '../Footer';
import { Toaster } from 'sonner';
import SEOHead from '../SEOHead';

const CouponService = () => {
  const { t, currentLanguage } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Coupon & Deals Advertising",
    "description": "Publicité sur sites de coupons et promotions. Audiences haute intention d'achat, 200M+ utilisateurs mensuels, ROI 3.2x.",
    "provider": {
      "@type": "Organization",
      "name": "OptimHub"
    }
  };

  const features = [
    {
      icon: Tag,
      title: tx(currentLanguage, 'Sites de Coupons Premium', 'Premium Coupon Sites', 'Premium-Gutschein-Sites'),
      description: tx(currentLanguage, 'Partenariats exclusifs avec les plus grands sites de coupons et promotions', 'Exclusive partnerships with the largest coupon and promotion sites', 'Exklusive Partnerschaften mit den größten Gutschein- und Promotion-Sites')
    },
    {
      icon: Users,
      title: tx(currentLanguage, 'Audiences Haute Intention', 'High-Intent Audiences', 'Hochintentive Zielgruppen'),
      description: tx(currentLanguage, 'Ciblez des utilisateurs déjà en phase d\'achat, recherchant activement des promotions', 'Target users already in purchase phase, actively searching for promotions', 'Zielen Sie auf Nutzer ab, die bereits in der Kaufphase sind und aktiv nach Angeboten suchen')
    },
    {
      icon: Shield,
      title: tx(currentLanguage, 'Placement Brand-Safe', 'Brand-Safe Placement', 'Markensichere Platzierung'),
      description: tx(currentLanguage, 'Environnement contrôlé et sécurisé pour protéger votre image de marque', 'Controlled and secure environment to protect your brand image', 'Kontrollierte und sichere Umgebung zum Schutz Ihres Markenimages')
    }
  ];

  const benefits = [
    {
      icon: Target,
      title: tx(currentLanguage, 'Conversion Élevée', 'High Conversion', 'Hohe Conversion'),
      description: tx(currentLanguage, 'Taux de conversion 3x supérieur grâce aux audiences prêtes à acheter', '3x higher conversion rate thanks to ready-to-buy audiences', '3x höhere Conversion-Rate dank kaufbereiter Zielgruppen')
    },
    {
      icon: TrendingUp,
      title: tx(currentLanguage, 'ROI Maximisé', 'Maximized ROI', 'Maximierter ROI'),
      description: tx(currentLanguage, 'Réduisez vos coûts d\'acquisition client avec des audiences qualifiées', 'Reduce customer acquisition costs with qualified audiences', 'Reduzieren Sie Kundenakquisitionskosten mit qualifizierten Zielgruppen')
    },
    {
      icon: Zap,
      title: tx(currentLanguage, 'Déploiement Rapide', 'Quick Deployment', 'Schnelle Bereitstellung'),
      description: tx(currentLanguage, 'Mise en ligne de vos campagnes en moins de 24h', 'Launch your campaigns in less than 24h', 'Starten Sie Ihre Kampagnen in weniger als 24 Stunden')
    }
  ];

  const stats = [
    { value: '85%', label: tx(currentLanguage, 'Taux de Conversion', 'Conversion Rate', 'Conversion-Rate') },
    { value: '200M+', label: tx(currentLanguage, 'Utilisateurs Mensuels', 'Monthly Users', 'Monatliche Nutzer') },
    { value: '3.2x', label: tx(currentLanguage, 'ROI Moyen', 'Average ROI', 'Durchschnittlicher ROI') }
  ];

  const customerJourney = [
    {
      step: 1,
      title: tx(currentLanguage, 'Recherche Active', 'Active Search', 'Aktive Suche'),
      description: tx(currentLanguage, 'L\'utilisateur recherche activement des coupons et promotions', 'User actively searches for coupons and promotions', 'Nutzer sucht aktiv nach Gutscheinen und Aktionen')
    },
    {
      step: 2,
      title: tx(currentLanguage, 'Découverte', 'Discovery', 'Entdeckung'),
      description: tx(currentLanguage, 'Votre offre apparaît au moment optimal d\'intention d\'achat', 'Your offer appears at the optimal moment of purchase intent', 'Ihr Angebot erscheint zum optimalen Zeitpunkt der Kaufabsicht')
    },
    {
      step: 3,
      title: tx(currentLanguage, 'Conversion', 'Conversion', 'Conversion'),
      description: tx(currentLanguage, 'L\'utilisateur convertit avec un taux élevé grâce à la promotion', 'User converts with high rate thanks to the promotion', 'Nutzer konvertiert mit hoher Rate dank der Promotion')
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title={tr(currentLanguage, "Coupon & Deals Advertising | Sites de Promotions & Bons de Réduction - OptimHub")}
        description={tr(currentLanguage, "Publicité sur sites de coupons premium. Audiences haute intention d'achat, 200M+ utilisateurs/mois, ROI 3.2x. Content commerce optimisé pour les promotions. CPC vers CPA.")}
        keywords="coupon advertising, deals advertising, promotion sites ads, coupon sites marketing, voucher advertising, discount codes ads, high intent advertising, content commerce coupons"
        ogImage="https://images.pexels.com/photos/7563655/pexels-photo-7563655.jpeg?w=1200"
        routeKey="serviceCoupon"
        lang={currentLanguage}
        schemaData={schemaData}
      />
      <Header />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-cyan-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-blue-100 text-blue-700 hover:bg-blue-200">
                {tx(currentLanguage, 'Coupon & Promotions', 'Coupon & Promotions', 'Gutschein & Aktionen')}
              </Badge>
              <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
                Coupon & Deals
                <span className="block text-blue-600 mt-2">
                  {tx(currentLanguage, 'Advertising', 'Advertising', 'Werbung')}
                </span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                {tx(currentLanguage, 'Placez votre marque sur les sites de coupons et promotions fréquentés par des consommateurs hautement engagés. En tant que traffic brokers, nous achetons du trafic au CPC et vous ne payez qu\'au CPA - vous ne payez que pour les conversions réelles.', 'Place your brand on coupon and promotion sites frequented by highly engaged consumers. As traffic brokers, we buy traffic at CPC and you only pay at CPA - you only pay for real conversions.', 'Platzieren Sie Ihre Marke auf Gutschein- und Promotion-Sites, die von hoch engagierten Verbrauchern besucht werden. Als Traffic-Broker kaufen wir Traffic zu CPC und Sie zahlen nur zu CPA - Sie zahlen nur für echte Conversions.')
                }
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  onClick={() => window.location.href = `${routePath('home', currentLanguage)}#contact`}
                  className="bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center"
                >
                  {tx(currentLanguage, 'Commencer maintenant', 'Get Started', 'Jetzt starten')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </a>
                <a 
                  href="mailto:contact@optimhub.com?subject=Demande de démo Coupon & Deals"
                  className="border-2 border-blue-200 text-blue-700 px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-colors duration-200 inline-flex items-center justify-center"
                >
                  {tx(currentLanguage, 'Voir la démo', 'Watch Demo', 'Demo ansehen')}
                </a>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/7563655/pexels-photo-7563655.jpeg"
                alt="Coupon & Deals Advertising"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">{stat.value}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              {tx(currentLanguage, 'Placements Coupon Premium', 'Premium Coupon Placements', 'Premium-Gutschein-Platzierungen')}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {tx(currentLanguage, 'Capturez l\'attention des consommateurs au moment où ils recherchent activement des promotions et sont prêts à acheter.', 'Capture consumers\' attention when they\'re actively searching for promotions and ready to buy.', 'Erfassen Sie die Aufmerksamkeit der Verbraucher, wenn sie aktiv nach Aktionen suchen und bereit sind zu kaufen.')
              }
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <Card key={index} className="group hover:shadow-xl transition-all duration-300 border-0 bg-gradient-to-br from-white to-orange-50/30">
                  <CardHeader className="text-center pb-4">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-200 transition-colors duration-300">
                      <IconComponent className="w-8 h-8 text-blue-600" />
                    </div>
                    <CardTitle className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors duration-300">
                      {feature.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-600 leading-relaxed text-center">
                      {feature.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Customer Journey Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              {tx(currentLanguage, 'Parcours Client Optimisé', 'Optimized Customer Journey', 'Optimierte Customer Journey')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {customerJourney.map((step, index) => (
              <div key={index} className="text-center relative">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold">
                  {step.step}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{step.title}</h3>
                <p className="text-gray-600 leading-relaxed">{step.description}</p>
                {index < customerJourney.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-1/2 transform translate-x-1/2 w-full">
                    <ArrowRight className="w-6 h-6 text-blue-300 ml-16" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              {tx(currentLanguage, 'Pourquoi Coupon & Deals ?', 'Why Coupon & Deals?', 'Warum Coupon & Deals?')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                    <IconComponent className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{benefit.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-cyan-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            {tx(currentLanguage, 'Prêt à Booster Vos Conversions ?', 'Ready to Boost Your Conversions?', 'Bereit, Ihre Conversions zu steigern?')}
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            {tx(currentLanguage, 'Rejoignez des milliers de marques qui maximisent leur ROI avec notre réseau Coupon & Deals.', 'Join thousands of brands maximizing their ROI with our Coupon & Deals network.', 'Schließen Sie sich Tausenden von Marken an, die ihren ROI mit unserem Coupon & Deals-Netzwerk maximieren.')
            }
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              onClick={() => window.location.href = `${routePath('home', currentLanguage)}#contact`}
              className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-50 transition-colors duration-200 flex items-center justify-center"
            >
              {tx(currentLanguage, 'Démarrer maintenant', 'Start Now', 'Jetzt starten')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </a>
            <a 
              onClick={() => window.location.href = `${routePath('home', currentLanguage)}#contact`}
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/10 transition-colors duration-200 cursor-pointer"
            >
              {tx(currentLanguage, 'Parler à un expert', 'Talk to an Expert', 'Mit Experten sprechen')}
            </a>
          </div>
        </div>
      </section>
      <Footer />
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#fff',
            color: '#374151',
            border: '1px solid #e5e7eb',
          },
        }}
      />
    </div>
  );
};

export default CouponService;
